import { Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@core/auth.service';
import { AuthPathsService } from '@core/multi-tenant-auth/auth-paths.service';
import { InactivityService } from '@core/inactivity.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WINDOW } from '@core/window.token';

@Component({
  selector: 'app-session-timeout-warning-dialog',
  standalone: true,
  imports: [A11yModule, MatButtonModule, MatDialogModule, TranslateModule],
  templateUrl: 'session-timeout-warning-dialog.component.html',
})
export class SessionTimeoutWarningDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<SessionTimeoutWarningDialogComponent>);
  private readonly inactivityService = inject(InactivityService);
  private readonly authService = inject(AuthService);
  private readonly authPaths = inject(AuthPathsService);
  private readonly window = inject(WINDOW);
  private readonly destroyRef = inject(DestroyRef);

  readonly countdown = this.inactivityService.warningCountdown;

  readonly formattedCountdown = computed(() => {
    const seconds = this.countdown();
    if (seconds === null) {
      return '';
    }
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  });

  private readonly announcementText = signal('');
  readonly announcement = this.announcementText.asReadonly();
  private firstEffectRun = true;

  constructor() {
    effect(() => {
      const seconds = this.countdown();
      if (seconds === null) {
        this.dialogRef.close();
        return;
      }
      // Skip announcing the initial value, to give time for the text-to-speech to read the dialog title and content.
      // Prevents announcing 2 minutes remaining when 20+ seconds already passed since the dialog opened.
      if (this.firstEffectRun) {
        this.firstEffectRun = false;
        return;
      }
      // Announce every 30 seconds, and every 10 seconds when there are less than 30 seconds remaining.
      const atInterval = seconds % 30 === 0 || (seconds <= 30 && seconds % 10 === 0);
      if (atInterval) {
        this.announcementText.set(this.formattedCountdown());
      }
    });
  }

  onStaySignedIn(): void {
    this.inactivityService.keepAlive().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  onSignOut(): void {
    const portalType = this.authService.getCurrentPortalType();
    // Force navigation to the logout page to prevent any modal/countdown side-effects or race conditions from the inactivity service.
    this.window.location.assign(this.authPaths.getLogoutPath(portalType));
  }
}
